import { useMemo } from 'react';

import { usePsfSharePageData, usePsfSharePageFcm } from 'src/app/pages/psf/share/pageContext';
import { SHARE_CHANNELS } from 'src/app/pages/psf/share/shared/consts';
import type { ShareChannel } from 'src/app/pages/psf/share/shared/types';
import { useQueryParamValue } from 'src/app/shared/hooks/location';
import { useFilteredShareChannels } from 'src/app/shared/hooks/share';

type OldShareChannelName = Exclude<
	ReturnType<typeof usePsfSharePageFcm>['shareButtonsListNonInitial'][0],
	ShareChannel
>;
type OldShareChannelNameFromQuery = 'copylink' | 'facebook_message' | 'instagram_story';
const OLD_TO_NEW_SHARE_CHANNEL_MAPPING: Record<string, ShareChannel> = {
	copylink: 'copy',
	facebook_message: 'facebookMessenger',
	instagram_story: 'instagram',
} satisfies Record<OldShareChannelName | OldShareChannelNameFromQuery, ShareChannel>;

function mapOldShareChannels(channels: ReadonlyArray<OldShareChannelName | ShareChannel>): readonly ShareChannel[] {
	// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
	return channels.map((name) => OLD_TO_NEW_SHARE_CHANNEL_MAPPING[name] || name);
}

export function useShareChannels(): readonly ShareChannel[] {
	const buttons = useQueryParamValue('buttons');
	const {
		data: { initial, isTiktokChannelEnabled },
	} = usePsfSharePageData();
	const { shareButtonsListInitial, shareButtonsListNonInitial, psfOnlyShareButtonList } = usePsfSharePageFcm();
	const {
		data: {
			petition: { geoReach },
		},
	} = usePsfSharePageData();

	const channels = useMemo(() => {
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-unsafe-type-assertion
		const fromQuery = (buttons ? buttons.split(',') : []).filter(
			(name) => SHARE_CHANNELS[name as ShareChannel],
		) as readonly ShareChannel[];

		if (fromQuery.length > 0) {
			return fromQuery;
		}

		// FIXME we have 3 FCs that list the channels in different ways, we should unify them
		if (psfOnlyShareButtonList.length > 0) {
			return psfOnlyShareButtonList;
		}
		return initial ? shareButtonsListInitial : mapOldShareChannels(shareButtonsListNonInitial);
	}, [buttons, initial, shareButtonsListInitial, shareButtonsListNonInitial, psfOnlyShareButtonList]);

	const filters = useMemo(
		() => ({
			...SHARE_CHANNELS,
			nextdoor: geoReach === 'local',
			tiktok: isTiktokChannelEnabled,
		}),
		[geoReach, isTiktokChannelEnabled],
	);

	return useFilteredShareChannels(channels, filters);
}
